import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { login as loginRequest, signup as signupRequest } from '../../services/auth';
import { setToken } from '../../services/storage';
import { sendTokenToExtension } from '../../services/extensionHandoff';
import { useAuth } from '../../hooks/auth';
import './AuthCard.css';

function GoogleIcon() {
  return (
    <svg className="auth-google-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

function EyeIcon({ open }) {
  if (open) {
    return (
      <svg className="auth-eye-icon" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    );
  }

  return (
    <svg className="auth-eye-icon" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
      <path d="M4 4l16 16" />
    </svg>
  );
}

function AuthCard({ mode }) {
  const isSignup = mode === 'signup';
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [searchParams] = useSearchParams();
  const source = searchParams.get('source');
  const extensionId = searchParams.get('extensionId');
  const oauthError = searchParams.get('error');
  const isExtensionFlow = source === 'extension' && Boolean(extensionId);

  const authQuery = isExtensionFlow
    ? `?source=extension&extensionId=${encodeURIComponent(extensionId)}`
    : '';

  const [step, setStep] = useState('choice');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState(() => {
    if (oauthError === 'account_exists') {
      return 'An account with this email already exists';
    }
    if (oauthError === 'google_auth_failed') {
      return 'Google sign-in failed. Please try again.';
    }
    return '';
  });
  const [loading, setLoading] = useState(false);
  const [extensionDone, setExtensionDone] = useState(false);

  const title = isSignup ? 'Sign Up' : 'Log in';
  const submitLabel = isSignup ? 'Sign up' : 'Log in';
  const footerPrompt = isSignup ? 'Already have an account?' : "Don't have an account?";
  const footerLinkLabel = isSignup ? 'Log in' : 'Sign up';
  const footerTo = `${isSignup ? '/login' : '/signup'}${authQuery}`;

  const EMAIL_REGEX = /^\S+@\S+\.\S+$/;
  const MIN_PASSWORD_LENGTH = 8;

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');

    const trimmedEmail = email.trim();
    const trimmedName = name.trim();

    if (isSignup) {
      if (!trimmedName) {
        setError('Name is required');
        return;
      }

      if (!trimmedEmail || !EMAIL_REGEX.test(trimmedEmail)) {
        setError('Valid email is required');
        return;
      }

      if (!password || password.length < MIN_PASSWORD_LENGTH) {
        setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
        return;
      }

      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
    } else if (!trimmedEmail || !password) {
      setError('Email and password are required');
      return;
    }

    setLoading(true);
    try {
      const request = isSignup ? signupRequest : loginRequest;
      const data = await request(
        isSignup
          ? { email: trimmedEmail, password, name: trimmedName }
          : { email: trimmedEmail, password }
      );
      setToken(data.token);

      if (isExtensionFlow) {
        try {
          await sendTokenToExtension(extensionId, data.token);
        } catch (handoffError) {
          console.warn('Extension handoff failed', handoffError);
        }
        setExtensionDone(true);
        return;
      }

      setSession({ email: data.email, name: data.name });
      navigate('/profile');
    } catch (err) {
      setError(err.message || 'Internal server error');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      <Link to="/" className="auth-logo">
        <img src="/logo.svg" alt="" className="auth-logo-icon" width="40" height="40" />
        <span className="al-logo">Numus</span>
      </Link>
      <div className="auth-card">
        {extensionDone ? (
          <>
            <h1 className="auth-title">You&apos;re signed in</h1>
            <p className="auth-extension-done">
              You can return to the Numus extension. Your original tab should be active again.
            </p>
          </>
        ) : (
          <>
            <h1 className="auth-title">{title}</h1>

            {step === 'choice' ? (
              <div className="auth-choice">
                <button
                  type="button"
                  className="auth-btn auth-btn--google"
                  onClick={() => {
                    window.location.href = `/api/v1/users/google${authQuery}`;
                  }}
                >
                  <GoogleIcon />
                  Continue with Google
                </button>

                <div className="auth-divider" role="separator" aria-label="or">
                  <span>OR</span>
                </div>

                <button
                  type="button"
                  className="auth-btn auth-btn--primary"
                  onClick={() => {
                    setError('');
                    setStep('email');
                  }}
                >
                  Continue with Email
                </button>

                {error ? <p className="auth-error">{error}</p> : null}
              </div>
            ) : (
              <form className="auth-form" onSubmit={handleSubmit} noValidate>
                <button
                  type="button"
                  className="auth-back"
                  onClick={() => {
                    setError('');
                    setStep('choice');
                  }}
                >
                  ← Back
                </button>

                {isSignup ? (
                  <label className="auth-field">
                    <span>Name</span>
                    <input
                      type="text"
                      name="name"
                      autoComplete="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </label>
                ) : null}

                <label className="auth-field">
                  <span>Email</span>
                  <input
                    type="email"
                    name="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </label>

                <label className="auth-field">
                  <span>Password</span>
                  <div className="auth-password-wrap">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      autoComplete={isSignup ? 'new-password' : 'current-password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={isSignup ? MIN_PASSWORD_LENGTH : undefined}
                    />
                    <button
                      type="button"
                      className="auth-eye-btn"
                      onClick={() => setShowPassword((prev) => !prev)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      <EyeIcon open={showPassword} />
                    </button>
                  </div>
                </label>

                {isSignup ? (
                  <label className="auth-field">
                    <span>Confirm password</span>
                    <div className="auth-password-wrap">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        minLength={MIN_PASSWORD_LENGTH}
                      />
                      <button
                        type="button"
                        className="auth-eye-btn"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                      >
                        <EyeIcon open={showConfirmPassword} />
                      </button>
                    </div>
                  </label>
                ) : null}

                {error ? <p className="auth-error">{error}</p> : null}

                <button type="submit" className="auth-btn auth-btn--primary" disabled={loading}>
                  {loading ? 'Please wait…' : submitLabel}
                </button>
              </form>
            )}

            <p className="auth-footer">
              {footerPrompt}{' '}
              <Link to={footerTo} className="auth-footer-link">
                {footerLinkLabel}
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default AuthCard;
